import { useEffect, useMemo, useState } from 'react';
import useTerrainSettings from './useTerrainSettings';
import { applyDemSurface, profileToSurface, terrainApi } from '../lib/terrain-api';

const lineMetres = ([a, b]) => {
  const mLon = 111320 * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return Math.hypot((b[0] - a[0]) * mLon, (b[1] - a[1]) * 110540);
};

/**
 * DEM surface for a SectionView model entry ({ model, cells, line }) when the Inversion
 * panel's "Use project DEM for surface" is on and the project has a DEM: the DEM profile
 * along the model line (POST /dem/profile, ~200 samples) as [[x, z]...] in model x.
 *
 * Returns { surface, cells, model, applied }: `cells` hang from the DEM when the model has
 * no topography of its own (lib/terrain-api applyDemSurface), `model` drops electrode z
 * then (markers follow the DEM), `applied` says whether that happened.
 */
export default function useSectionTerrain(entry, cells) {
  const t = useTerrainSettings();
  const line = entry?.line;
  const xRange = entry?.model?.xRange;
  const enabled = !!(t.useDemSurface && t.demDatasetId && Array.isArray(line) && line.length === 2 && xRange);
  const key = enabled ? `${t.demDatasetId}|${line[0].join(',')}|${line[1].join(',')}|${xRange.join(',')}` : null;
  const [surface, setSurface] = useState(null);

  // `key` captures demDatasetId, line and xRange; the effect reads them back from it.
  useEffect(() => {
    if (!key) { setSurface(null); return undefined; }
    const [demId, a, b, xr] = key.split('|');
    const ln = [a.split(',').map(Number), b.split(',').map(Number)];
    const range = xr.split(',').map(Number);
    let active = true;
    const step = Math.max(0.5, lineMetres(ln) / 200);
    terrainApi.profile(demId, { line: ln, step })
      .then((r) => { if (active) setSurface(profileToSurface(r.data, range)); })
      .catch(() => { if (active) setSurface(null); });
    return () => { active = false; };
  }, [key]);

  return useMemo(() => {
    if (!surface || !surface.length || !cells) return { surface: null, cells, model: entry?.model, applied: false };
    const hung = applyDemSurface(cells, surface);
    const applied = hung !== cells;
    const model = applied && entry?.model?.electrodes
      ? { ...entry.model, electrodes: entry.model.electrodes.map(({ z, ...e }) => e) }
      : entry?.model;
    return { surface, cells: hung, model, applied };
  }, [surface, cells, entry]);
}
